'use client';

/**
 * Full-height banner slider — each slide is one full-bleed background
 * image filling the visible viewport, with a white copy card (kicker,
 * title, description, CTA) floating over it. Two or more slides turn it
 * into a carousel whose prev/next arrows and dots sit pinned, centred,
 * at the bottom edge.
 *
 * <pre>
 *   ┌──────────────────────────────────────────────────────────┐
 *   │                                                          │
 *   │     ┌──────────────────┐                                 │
 *   │     │ Kicker           │         background image        │
 *   │     │ Title on two     │                                 │
 *   │     │ lines            │                                 │
 *   │     │ Description      │                                 │
 *   │     │ [ CTA ]          │                                 │
 *   │     └──────────────────┘                                 │
 *   │                          ‹  ›                            │
 *   │                       ▬  •  •  •                         │
 *   └──────────────────────────────────────────────────────────┘
 * </pre>
 *
 * <p>The card is a fixed white surface with fixed dark ink — it sits on
 * an arbitrary photo, so it deliberately does NOT follow the theme's
 * text/surface tokens (a dark theme would otherwise paint white text on
 * the white card). The typeface is the theme's: the card rides
 * {@code --font-family-body} and the title {@code --font-family-display},
 * so switching the theme font re-types the banner. Sizes follow the
 * design: 15px kicker, 28px SemiBold title, 14px Regular description
 * and CTA.
 *
 * <p>Full height, the carousel behaviour and the pinned controls come
 * from {@link FullHeightSlider}; this block owns the slide content and
 * per-slide campaign gating ({@code expiresAt} / audience groups).
 */
import { useMemo, type CSSProperties } from 'react';
import { Button } from '@akademi/ui';
import {
  isVisible,
  useBlockPart,
  useCurrentUser,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { tx } from '../_shared/coerce';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import {
  FullHeightFrame,
  FullHeightSlider,
  type FullHeightSliderSettings,
} from '../_shared/FullHeightSlider';

type Localized = string | Record<string, string>;

export type HeroFullHeightBannerCardPosition = 'left' | 'center' | 'right';
export type HeroFullHeightBannerImagePosition = 'center' | 'left' | 'right';

export interface HeroFullHeightBannerSlide extends CampaignAudience {
  kicker?: Localized;
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  /** Open the CTA in a new tab. */
  ctaNewTab?: boolean;
  /** Full-bleed background image. */
  image?: string;
  /** Optional portrait crop served below 768px. */
  mobileImage?: string;
  /** Leave empty when the image is decorative. */
  imageAlt?: string;
  /** Which edge of the image survives the `cover` crop on narrow screens. */
  imagePosition?: HeroFullHeightBannerImagePosition;
  cardPosition?: HeroFullHeightBannerCardPosition;
}

export interface HeroFullHeightBannerProps extends FullHeightSliderSettings {
  slides?: HeroFullHeightBannerSlide[];
  /** Heading level of the FIRST slide's title — `h1` only when this is the
   *  page's opener. Later slides always use `h2` so a page never carries
   *  several h1s. */
  titleTag?: 'h1' | 'h2';
}

const IMAGE_SRCSET_WIDTHS = [800, 1200, 1600, 2400];

const JUSTIFY: Record<HeroFullHeightBannerCardPosition, CSSProperties['justifyContent']> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

// Hover and the breakpoint can't live in an inline style object, so they go
// in a scoped stylesheet (same convention as proof/Testimonials).
const HERO_FULL_HEIGHT_BANNER_CSS = `
.hfhb-card .hfhb-cta {
  background-color: #333333;
  color: #ffffff;
}
.hfhb-card .hfhb-cta:hover { background-color: #1f1f1f; }
@media (max-width: 767px) {
  .hfhb-card { padding: 40px 24px 32px !important; }
}
`;

const srcSetOf = (url: string) =>
  IMAGE_SRCSET_WIDTHS.map((w) => `${resizeImageUrl(url, `${w}x0`)} ${w}w`).join(', ');

const hasContent = (s: HeroFullHeightBannerSlide) =>
  Boolean(tx(s.kicker) || tx(s.title) || tx(s.description) || tx(s.ctaLabel) || s.image);

export function HeroFullHeightBanner({
  slides: rawSlides,
  titleTag = 'h2',
  ...settings
}: HeroFullHeightBannerProps) {
  const currentUser = useCurrentUser<CurrentUser>();

  // Half-authored (empty) slides would render as bare dark screens; expired
  // or audience-restricted ones must not render at all.
  const slides = useMemo(
    () =>
      (Array.isArray(rawSlides) ? rawSlides : [])
        .filter((s) => s && hasContent(s))
        .filter((s) => isVisible(s, currentUser)),
    [rawSlides, currentUser],
  );

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: HERO_FULL_HEIGHT_BANNER_CSS }} />
      <FullHeightSlider
        {...settings}
        slides={slides}
        renderSlide={(slide, i, { hasControls }) => (
          <BannerSlide
            slide={slide}
            index={i}
            hasControls={hasControls}
            titleTag={i === 0 && titleTag === 'h1' ? 'h1' : 'h2'}
          />
        )}
      />
    </>
  );
}

// =============================================================================
// One slide: background image + the white copy card
// =============================================================================

function BannerSlide({
  slide,
  index,
  hasControls,
  titleTag,
}: {
  slide: HeroFullHeightBannerSlide;
  index: number;
  hasControls: boolean;
  titleTag: 'h1' | 'h2';
}) {
  const part = useBlockPart();

  const kickerText = tx(slide.kicker);
  const titleText = tx(slide.title);
  const descriptionText = tx(slide.description);
  const ctaText = tx(slide.ctaLabel);
  const hasCard = Boolean(kickerText || titleText || descriptionText || ctaText);
  const TitleTag = titleTag;
  const alt = slide.imageAlt?.trim() ?? '';
  const cardPosition = slide.cardPosition ?? 'left';
  // Only the first slide is the LCP candidate; the rest wait their turn.
  const first = index === 0;

  return (
    <>
      {slide.image ? (
        <picture>
          {slide.mobileImage ? (
            <source media="(max-width: 767px)" srcSet={srcSetOf(slide.mobileImage)} sizes="100vw" />
          ) : null}
          <img
            {...part('media', index)}
            src={resizeImageUrl(slide.image, '1600x0')}
            srcSet={srcSetOf(slide.image)}
            sizes="100vw"
            alt={alt}
            aria-hidden={alt ? undefined : true}
            loading={first ? 'eager' : 'lazy'}
            fetchPriority={first ? 'high' : undefined}
            decoding="async"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: -1,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: slide.imagePosition ?? 'center',
            }}
          />
        </picture>
      ) : (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            background: 'linear-gradient(135deg, #151923 0%, #101010 100%)',
          }}
        />
      )}

      <FullHeightFrame
        hasControls={hasControls}
        style={{
          alignItems: 'center',
          justifyContent: JUSTIFY[cardPosition] ?? 'flex-start',
        }}
      >
        {hasCard ? (
          <div
            {...part('body', index)}
            className="hfhb-card"
            style={{
              width: '100%',
              maxWidth: 434,
              boxSizing: 'border-box',
              padding: '67px 57px 39px',
              background: '#FFFFFF',
              borderRadius: 5,
              opacity: 1,
              fontFamily: 'var(--font-family-body, inherit)',
              color: '#2b2b2b',
            }}
          >
            {kickerText ? (
              <p
                {...part('kicker', index)}
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: '#707070',
                }}
              >
                {kickerText}
              </p>
            ) : null}
            {titleText ? (
              <TitleTag
                {...part('title', index)}
                style={{
                  margin: kickerText ? '19px 0 0' : 0,
                  fontFamily: 'var(--font-family-display, inherit)',
                  fontSize: 28,
                  fontWeight: 600,
                  lineHeight: '34px',
                  color: '#2b2b2b',
                }}
              >
                {titleText}
              </TitleTag>
            ) : null}
            {descriptionText ? (
              <p
                {...part('subtitle', index)}
                style={{
                  margin: kickerText || titleText ? '24px 0 0' : 0,
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: '#2b2b2b',
                  whiteSpace: 'pre-line',
                }}
              >
                {descriptionText}
              </p>
            ) : null}
            {ctaText ? (
              <Button
                {...part('cta', index)}
                href={slide.ctaHref || '#'}
                radius="none"
                className="hfhb-cta"
                {...(slide.ctaNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                style={{
                  // Layout doesn't lean on the Button's utility classes, so
                  // the card keeps its rhythm on a host without Tailwind.
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  textDecoration: 'none',
                  marginTop: kickerText || titleText || descriptionText ? 37 : 0,
                  height: 42,
                  minWidth: 146,
                  padding: '0 25px',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 400,
                }}
              >
                {ctaText}
              </Button>
            ) : null}
          </div>
        ) : null}
      </FullHeightFrame>
    </>
  );
}
